import {
  WifiIcon,
  LightBulbIcon,
  SunIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  PresentationChartBarIcon,
  PrinterIcon,
  Battery100Icon,
  ShoppingBagIcon,
  SparklesIcon,
  EyeIcon,
  FireIcon,
  TruckIcon,
  FilmIcon,
  MapIcon,
} from "@heroicons/react/24/outline";

import {
  FaPlug,
  FaLightbulb,
  FaSnowflake,
  FaRestroom,
  FaWater,
  FaChair,
  FaCouch,
  FaRProject,
  FaHeadphones,
  FaFirstAid,
  FaBraille,
  FaAssistiveListeningSystems,
  FaMailBulk,
  FaUsers,
  FaDog,
  FaUmbrellaBeach,
  FaCoffee,
  FaWheelchair,
  FaVolumeMute,
} from "react-icons/fa";

import {
  MdMeetingRoom,
  MdVideocam,
  MdSupport,
  MdOutlineKitchen,
  MdOutlineChildCare,
  MdOutlineSecurity,
  MdLocalHospital,
  MdScience,
  MdOutlineNature,
  MdStore,
  MdMicrowave,
  MdOutlineDesk,
  MdForklift,
  MdOutlinePersonOutline,
  MdOutlineDirectionsBike,
  MdOutlineCleaningServices,
} from "react-icons/md";
import { PiLockersThin } from "react-icons/pi";
import { LuShowerHead } from "react-icons/lu";
import { IoCarOutline } from "react-icons/io5";
import { GrElevator } from "react-icons/gr";

const amenities = [
  // Basic Infrastructure
  {
    value: 1,
    title: "High-speed internet/Wi-Fi",
    hoverDescription: "Reliable high-speed connectivity",
    icon: <WifiIcon className="h-6 w-6" />,
    category: "Basic Infrastructure",
  },
  {
    value: 2,
    title: "Electrical outlets/USB ports",
    hoverDescription: "Ample power access throughout",
    icon: <FaPlug className="h-6 w-6" />,
    category: "Basic Infrastructure",
  },
  {
    value: 3,
    title: "Lighting (natural & artificial)",
    hoverDescription: "Well-lit workspace",
    icon: <FaLightbulb className="h-6 w-6" />,
    category: "Basic Infrastructure",
  },
  {
    value: 4,
    title: "HVAC systems",
    hoverDescription: "Climate controlled environment",
    icon: <FaSnowflake className="h-6 w-6" />,
    category: "Basic Infrastructure",
  },
  {
    value: 5,
    title: "Restrooms",
    hoverDescription: "Clean, well-maintained facilities",
    icon: <FaRestroom className="h-6 w-6" />,
    category: "Basic Infrastructure",
  },
  {
    value: 6,
    title: "Drinking water",
    hoverDescription: "Filtered water stations",
    icon: <FaWater className="h-6 w-6" />,
    category: "Basic Infrastructure",
  },

  // Furniture & Workstations
  {
    value: 7,
    title: "Adjustable desks",
    hoverDescription: "Sit-stand workstations",
    icon: <MdOutlineDesk className="h-6 w-6" />,
    category: "Furniture",
  },
  {
    value: 8,
    title: "Ergonomic chairs",
    hoverDescription: "Supportive seating",
    icon: <FaChair className="h-6 w-6" />,
    category: "Furniture",
  },
  {
    value: 9,
    title: "Private offices",
    hoverDescription: "Dedicated workspaces",
    icon: <BuildingOfficeIcon className="h-6 w-6" />,
    category: "Furniture",
  },
  {
    value: 10,
    title: "Open-plan workstations",
    hoverDescription: "Collaborative seating",
    icon: <UserGroupIcon className="h-6 w-6" />,
    category: "Furniture",
  },
  {
    value: 11,
    title: "Lounge seating",
    hoverDescription: "Comfortable casual areas",
    icon: <FaCouch className="h-6 w-6" />,
    category: "Furniture",
  },

  // Meeting & Collaboration Spaces
  {
    value: 12,
    title: "Conference rooms",
    hoverDescription: "Bookable meeting spaces",
    icon: <MdMeetingRoom className="h-6 w-6" />,
    category: "Meeting Spaces",
  },
  {
    value: 13,
    title: "Phone booths",
    hoverDescription: "Soundproof call pods",
    icon: <PhoneIcon className="h-6 w-6" />,
    category: "Meeting Spaces",
  },
  {
    value: 14,
    title: "Whiteboards",
    hoverDescription: "Collaboration tools",
    icon: <PresentationChartBarIcon className="h-6 w-6" />,
    category: "Meeting Spaces",
  },
  {
    value: 15,
    title: "Projectors",
    hoverDescription: "Presentation equipment",
    icon: <FaRProject className="h-6 w-6" />,
    category: "Meeting Spaces",
  },

  // Technology & Equipment
  {
    value: 16,
    title: "Printers/Scanners",
    hoverDescription: "Shared office equipment",
    icon: <PrinterIcon className="h-6 w-6" />,
    category: "Technology",
  },
  {
    value: 17,
    title: "IT support",
    hoverDescription: "Technical assistance",
    icon: <MdSupport className="h-6 w-6" />,
    category: "Technology",
  },
  {
    value: 18,
    title: "Video conferencing",
    hoverDescription: "Meeting technology",
    icon: <MdVideocam className="h-6 w-6" />,
    category: "Technology",
  },
  {
    value: 19,
    title: "Charging stations",
    hoverDescription: "Device power hubs",
    icon: <Battery100Icon className="h-6 w-6" />,
    category: "Technology",
  },

  // Kitchen & Break Areas
  {
    value: 20,
    title: "Coffee station",
    hoverDescription: "Complimentary beverages",
    icon: <FaCoffee className="h-6 w-6" />,
    category: "Kitchen",
  },
  {
    value: 21,
    title: "Microwave & fridge",
    hoverDescription: "Food prep appliances",
    icon: <MdMicrowave className="h-6 w-6" />,
    category: "Kitchen",
  },
  {
    value: 22,
    title: "Snack bar",
    hoverDescription: "Refreshment options",
    icon: <ShoppingBagIcon className="h-6 w-6" />,
    category: "Kitchen",
  },
  {
    value: 23,
    title: "Dining area",
    hoverDescription: "Designated eating space",
    icon: <MdOutlineKitchen className="h-6 w-6" />,
    category: "Kitchen",
  },

  // Wellness & Comfort
  {
    value: 24,
    title: "Natural lighting",
    hoverDescription: "Window access",
    icon: <SunIcon className="h-6 w-6" />,
    category: "Wellness",
  },
  {
    value: 25,
    title: "Air purification",
    hoverDescription: "Clean air systems",
    icon: <SparklesIcon className="h-6 w-6" />,
    category: "Wellness",
  },
  {
    value: 26,
    title: "Quiet rooms",
    hoverDescription: "Focus spaces",
    icon: <FaVolumeMute className="h-6 w-6" />,
    category: "Wellness",
  },
  {
    value: 27,
    title: "Nursing room",
    hoverDescription: "Parent care space",
    icon: <MdOutlineChildCare className="h-6 w-6" />,
    category: "Wellness",
  },

  // Security & Safety
  {
    value: 28,
    title: "24/7 access",
    hoverDescription: "Secure entry",
    icon: <MdOutlineSecurity className="h-6 w-6" />,
    category: "Security",
  },
  {
    value: 29,
    title: "Surveillance",
    hoverDescription: "Security cameras",
    icon: <EyeIcon className="h-6 w-6" />,
    category: "Security",
  },
  {
    value: 30,
    title: "Fire safety",
    hoverDescription: "Emergency systems",
    icon: <FireIcon className="h-6 w-6" />,
    category: "Security",
  },
  {
    value: 31,
    title: "First aid",
    hoverDescription: "Medical supplies",
    icon: <FaFirstAid className="h-6 w-6" />,
    category: "Security",
  },

  // Accessibility Features
  {
    value: 32,
    title: "Wheelchair access",
    hoverDescription: "ADA compliant",
    icon: <FaWheelchair className="h-6 w-6" />,
    category: "Accessibility",
  },
  {
    value: 33,
    title: "Braille signage",
    hoverDescription: "Tactile indicators",
    icon: <FaBraille className="h-6 w-6" />,
    category: "Accessibility",
  },
  {
    value: 34,
    title: "Assistive listening",
    hoverDescription: "Hearing support",
    icon: <FaAssistiveListeningSystems className="h-6 w-6" />,
    category: "Accessibility",
  },

  // Office Spaces
  {
    value: 35,
    title: "Mail handling",
    hoverDescription: "Package reception",
    icon: <FaMailBulk className="h-6 w-6" />,
    category: "Office Services",
  },
  {
    value: 36,
    title: "Business address",
    hoverDescription: "Professional location",
    icon: <MapIcon className="h-6 w-6" />,
    category: "Office Services",
  },

  // Industrial & Warehouse
  {
    value: 37,
    title: "Loading docks",
    hoverDescription: "Freight access",
    icon: <TruckIcon className="h-6 w-6" />,
    category: "Industrial",
  },
  {
    value: 38,
    title: "Forklifts",
    hoverDescription: "Material handling",
    icon: <MdForklift className="h-6 w-6" />,
    category: "Industrial",
  },

  // Retail & Storefront
  {
    value: 39,
    title: "POS systems",
    hoverDescription: "Payment processing",
    icon: <MdStore className="h-6 w-6" />,
    category: "Retail",
  },

  // Studios
  {
    value: 40,
    title: "Soundproofing",
    hoverDescription: "Acoustic treatment",
    icon: <FilmIcon className="h-6 w-6" />,
    category: "Studios",
  },
  {
    value: 41,
    title: "Studio lighting",
    hoverDescription: "Professional rigs",
    icon: <LightBulbIcon className="h-6 w-6" />,
    category: "Studios",
  },

  // Medical & Healthcare
  {
    value: 42,
    title: "Exam rooms",
    hoverDescription: "Patient care spaces",
    icon: <MdLocalHospital className="h-6 w-6" />,
    category: "Medical",
  },

  // Laboratories
  {
    value: 43,
    title: "Lab benches",
    hoverDescription: "Research workstations",
    icon: <MdScience className="h-6 w-6" />,
    category: "Labs",
  },

  // Co-Working & Shared
  {
    value: 44,
    title: "Community events",
    hoverDescription: "Networking opportunities",
    icon: <FaUsers className="h-6 w-6" />,
    category: "Community",
  },
  {
    value: 45,
    title: "Pet-friendly",
    hoverDescription: "Animal companions welcome",
    icon: <FaDog className="h-6 w-6" />,
    category: "Community",
  },

  // Home Office
  {
    value: 46,
    title: "Noise-canceling",
    hoverDescription: "Focus solutions",
    icon: <FaHeadphones className="h-6 w-6" />,
    category: "Home Office",
  },

  // Outdoor Workspaces
  {
    value: 47,
    title: "Rooftop terraces",
    hoverDescription: "Open-air workspaces",
    icon: <MdOutlineNature className="h-6 w-6" />,
    category: "Outdoor",
  },
  {
    value: 48,
    title: "Beachside setups",
    hoverDescription: "Casual work environments",
    icon: <FaUmbrellaBeach className="h-6 w-6" />,
    category: "Outdoor",
  },
  {
    value: 49,
    title: "Receptionist",
    hoverDescription: "Shared front desk reception",
    icon: <MdOutlinePersonOutline className="h-6 w-6" />,
    category: "Community",
  },
  {
    value: 50,
    title: "Storage",
    hoverDescription: "Private storage spaces/lockers",
    icon: <PiLockersThin className="h-6 w-6" />,
    category: "Community",
  },
  {
    value: 51,
    title: "Elevator access",
    hoverDescription: "Elevator access to all floors",
    icon: <GrElevator className="h-6 w-6" />,
    category: "Community",
  },
  {
    value: 52,
    title: "Car parking",
    hoverDescription: "Designated parking spaces",
    icon: <IoCarOutline className="h-6 w-6" />,
    category: "Community",
  },
  {
    value: 53,
    title: "Bike storage",
    hoverDescription: "Storage space for bicycles/motorcycles",
    icon: <MdOutlineDirectionsBike className="h-6 w-6" />,
    category: "Community",
  },
  {
    value: 54,
    title: "Shower facilities",
    hoverDescription: "Shared front desk",
    icon: <LuShowerHead className="h-6 w-6" />,
    category: "Office Cleaning",
  },
  {
    value: 55,
    title: "Disabled access",
    hoverDescription: "Shared front desk",
    icon: <FaWheelchair className="h-6 w-6" />,
    category: "Office Cleaning",
  },
  {
    value: 56,
    title: "Office cleaning",
    hoverDescription: "Shared cleaning services for all spaces",
    icon: <MdOutlineCleaningServices className="h-6 w-6" />,
    category: "Office Cleaning",
  },
];

export default amenities;
